import type { ReactNode } from 'react';
import { Box, Text, config } from 'folds';
import { Check, sizedIcon } from '$components/icons/phosphor';
import { SequenceCard } from '$components/sequence-card';
import { SettingTile } from '$components/setting-tile';
import { BetaNoticeBadge } from '$components/BetaNoticeBadge';
import { CreateRoomType } from './types';

type CreateRoomTypeSelectorProps = {
  value?: CreateRoomType;
  onSelect: (value: CreateRoomType) => void;
  disabled?: boolean;
  getIcon: (type: CreateRoomType) => ReactNode;
};
export function CreateRoomTypeSelector({
  value,
  onSelect,
  disabled,
  getIcon,
}: CreateRoomTypeSelectorProps) {
  return (
    <Box shrink="No" direction="Column" gap="100">
      <SequenceCard
        style={{ padding: config.space.S300 }}
        variant={value === CreateRoomType.TextRoom ? 'Primary' : 'SurfaceVariant'}
        direction="Column"
        gap="100"
        as="button"
        type="button"
        aria-pressed={value === CreateRoomType.TextRoom}
        onClick={() => onSelect(CreateRoomType.TextRoom)}
        disabled={disabled}
      >
        <SettingTile
          before={getIcon(CreateRoomType.TextRoom)}
          after={value === CreateRoomType.TextRoom && sizedIcon(Check)}
        >
          <Box gap="200" alignItems="Baseline">
            <Text size="H6" style={{ flexShrink: 0 }}>
              Chat Room
            </Text>
            <Text size="T300" priority="300" truncate>
              - Messages, photos, and videos.
            </Text>
          </Box>
        </SettingTile>
      </SequenceCard>
      <SequenceCard
        style={{ padding: config.space.S300 }}
        variant={value === CreateRoomType.VoiceRoom ? 'Primary' : 'SurfaceVariant'}
        direction="Column"
        gap="100"
        as="button"
        type="button"
        aria-pressed={value === CreateRoomType.VoiceRoom}
        onClick={() => onSelect(CreateRoomType.VoiceRoom)}
        disabled={disabled}
      >
        <SettingTile
          before={getIcon(CreateRoomType.VoiceRoom)}
          after={value === CreateRoomType.VoiceRoom && sizedIcon(Check)}
        >
          <Box gap="200" alignItems="Baseline">
            <Text size="H6" style={{ flexShrink: 0 }}>
              Voice Room
            </Text>
            <Text size="T300" priority="300" truncate>
              - Live audio and video conversations.
            </Text>
            <BetaNoticeBadge />
          </Box>
        </SettingTile>
      </SequenceCard>
      <SequenceCard
        style={{ padding: config.space.S300 }}
        variant={value === CreateRoomType.ForumRoom ? 'Primary' : 'SurfaceVariant'}
        direction="Column"
        gap="100"
        as="button"
        type="button"
        aria-pressed={value === CreateRoomType.ForumRoom}
        onClick={() => onSelect(CreateRoomType.ForumRoom)}
        disabled={disabled}
      >
        <SettingTile
          before={getIcon(CreateRoomType.ForumRoom)}
          after={value === CreateRoomType.ForumRoom && sizedIcon(Check)}
        >
          <Box gap="200" alignItems="Baseline">
            <Text size="H6" style={{ flexShrink: 0 }}>
              Forum Room
            </Text>
            <Text size="T300" priority="300" truncate>
              - Conversations split in topics.
            </Text>
            <BetaNoticeBadge />
          </Box>
        </SettingTile>
      </SequenceCard>
    </Box>
  );
}
